import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/tailwind.utils'

type FolderCreatePopoverProps = {
  // Resolves true on success (popover closes) or false on failure (popover stays open).
  onCreate: (name: string) => Promise<boolean>
  nameError?: string | null
  onClearNameError?: () => void
}

export const FolderCreatePopover = ({
  onCreate,
  nameError,
  onClearNameError,
}: FolderCreatePopoverProps) => {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isValid = name.trim().length > 0

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) {
      setName('')
      onClearNameError?.()
    }
  }

  const handleSubmit = async () => {
    if (!isValid || isSubmitting) return
    setIsSubmitting(true)
    const created = await onCreate(name.trim())
    setIsSubmitting(false)
    if (created) handleOpenChange(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="text-primary h-7 px-2">
          <Plus className="h-3.5 w-3.5" />
          New collection
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-collection-name">Name</Label>
            <Input
              id="new-collection-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                onClearNameError?.()
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              aria-invalid={!!nameError}
              className={cn(
                'border border-border',
                nameError && 'border-destructive focus-visible:ring-destructive',
              )}
              autoFocus
            />
            {nameError && <p className="text-sm text-destructive">{nameError}</p>}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSubmit} disabled={!isValid || isSubmitting}>
              Create
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
